import Api from 'entities/helpers/api';
import ErrorLogger from 'entities/helpers/error-log-helper';
import { useIdentity } from 'base/identity-resolver';
import { useState, useEffect, useCallback } from 'react';
import {
  getJourneyTimeoutsForUser,
  createJourneyTimeout,
  updateJourneyTimeout,
  cancelJourneyTimeout,
} from '../utils/api-requests/use-journey-timeouts-requests';
import { getDateInTimezone, getDateString } from 'base/new-utils';

// Module-scoped dedup for auto-resume attempts: the home page mounts multiple
// components (JourneyPauseContainer, Upcoming, NextFiveDays) that each call
// this hook independently, so on load we can race N concurrent PUTs against
// the same timeout id. The second/third PUT lands on a resource that's already
// transitioned and 404s or 409s, which used to leave that hook instance stuck.
// Track ids we've already attempted this session so only one PUT fires per
// timeout — SIG-860.
const attemptedResumes = new Set();

// Custom Hook to handle all Cadence Pausing/Journey Timeout API Requests
const useJourneyTimeoutsApi = () => {
  const { user = {}, org } = useIdentity();
  const [inProgressJourneyTimeout, setInProgressJourneyTimeout] = useState(null);
  const [futureJourneyTimeout, setFutureJourneyTimeout] = useState(null);
  const [businessDaysUntilFutureTimeout, setBusinessDaysUntilFutureTimeout] = useState(0);
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    const fetchHolidays = async () => {
      let holidaysFromApi = {};
      try {
        holidaysFromApi = await Api.JOURNEYS.JOURNEY_TIMEOUT_HOLIDAYS.get();
        const datesArray = Object.values(holidaysFromApi).map((date) => getDateInTimezone(date));
        setHolidays(datesArray);
      } catch (e) {
        // don't throw an error, just log it
        console.error('Error fetching holidays', e);
      }
    };

    fetchHolidays();
  }, []);

  // Function to fetch and set user timeouts
  const getAndSetUserTimeouts = useCallback(async () => {
    if (!user.id) return;

    try {
      // Guard against a failed fetch resolving to something other than the
      // expected shape (e.g. an Alert side-effect return value) — destructuring
      // undefined here used to crash every consumer of this hook on any GET failure.
      const { activeJourneyTimeout = null, scheduledJourneyTimeouts = [] } =
        (await getJourneyTimeoutsForUser(user.id)) || {};

      // If the pause's return date has arrived (or passed), auto-resume so the user
      // sees tasks immediately. SIG-860: clear the paused banner state OPTIMISTICALLY
      // (before we know if the PUT succeeds), so a failing/racing PUT can never keep
      // a fundraiser locked out of their tasks. If the backend didn't transition,
      // the next natural refetch will reconcile — but the customer-visible symptom
      // (banner stuck, zero tasks) is impossible from this path forward.
      if (activeJourneyTimeout?.ends_on && activeJourneyTimeout.ends_on <= getDateString()) {
        setInProgressJourneyTimeout(null);
        setFutureJourneyTimeout(null);
        if (scheduledJourneyTimeouts?.length) {
          // starts_on is an ISO date string ("YYYY-MM-DD"); numeric subtraction
          // returns NaN and leaves the array unsorted. localeCompare on ISO
          // strings sorts chronologically.
          const sortedScheduledTimeouts = scheduledJourneyTimeouts.sort((a, b) =>
            a.starts_on.localeCompare(b.starts_on)
          );
          setFutureJourneyTimeout(sortedScheduledTimeouts[0]);
        }

        // Dedup the PUT across hook instances that mount on the same page.
        if (!attemptedResumes.has(activeJourneyTimeout.id)) {
          attemptedResumes.add(activeJourneyTimeout.id);
          try {
            const payload = { ...activeJourneyTimeout, ends_on: getDateString() };
            await updateJourneyTimeout(payload, activeJourneyTimeout.id);
          } catch (e) {
            // Escalate to Sentry so we know when the backend transition fails —
            // without this, silent 4xx/5xx responses left users stuck (SIG-860).
            ErrorLogger.error('Error auto-resuming expired pause', {
              extra: { error: e, timeoutId: activeJourneyTimeout.id, page_name: 'journeys' },
            });
          }
        }
        return;
      }

      setInProgressJourneyTimeout(activeJourneyTimeout);
      setFutureJourneyTimeout(null);
      if (!activeJourneyTimeout && scheduledJourneyTimeouts?.length) {
        const sortedScheduledTimeouts = scheduledJourneyTimeouts.sort((a, b) => a.starts_on.localeCompare(b.starts_on));
        setFutureJourneyTimeout(sortedScheduledTimeouts[0]);
      }
    } catch (e) {
      // Any unexpected failure fetching/deriving pause state is logged rather than
      // thrown, so it can never crash a consumer of this hook or mask an error from
      // a concurrent handleCreate/handleUpdate call that refetches in a `finally`.
      ErrorLogger.error('Error fetching journey timeouts', {
        extra: { error: e, userId: user.id, page_name: 'journeys' },
      });
    }
  }, [user.id]);

  // Populate state on initial render
  useEffect(() => {
    getAndSetUserTimeouts();
  }, [getAndSetUserTimeouts]);

  // API Calls
  const handleCreate = async (start, end) => {
    const payload = {
      owner_user_id: user.id,
      oid: org.id,
      starts_on: start,
      ends_on: end,
    };
    try {
      await createJourneyTimeout(payload);
    } finally {
      // Always refetch, even on failure, so the UI reflects true server state
      // (e.g. a rejected "create" shouldn't leave stale/incorrect pause state
      // displayed). Safe because getAndSetUserTimeouts never throws.
      await getAndSetUserTimeouts();
    }
  };

  const handleUpdate = async (start, end, currentTimeout) => {
    const payload = {
      ...currentTimeout,
      starts_on: start,
      ends_on: end,
    };
    try {
      await updateJourneyTimeout(payload, currentTimeout.id);
    } finally {
      await getAndSetUserTimeouts();
    }
  };

  const handleCancel = async () => {
    // should only be called for FUTURE timeout
    await cancelJourneyTimeout(futureJourneyTimeout.id);
    await getAndSetUserTimeouts();
  };

  const handleResume = async () => {
    const payload = {
      ...inProgressJourneyTimeout,
      ends_on: getDateString(), // ends_on: today
    };

    await updateJourneyTimeout(payload, inProgressJourneyTimeout.id);
    await getAndSetUserTimeouts();
  };

  useEffect(() => {
    if (futureJourneyTimeout && futureJourneyTimeout?.starts_on) {
      const pauseStart = getDateInTimezone(futureJourneyTimeout?.starts_on);
      let totalBusinessDays = 0;

      for (let current = new Date(); current <= pauseStart; current.setDate(current.getDate() + 1)) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          totalBusinessDays++;
        }
      }
      setBusinessDaysUntilFutureTimeout(totalBusinessDays);
    }
  }, [futureJourneyTimeout]);

  return {
    holidays,
    futureJourneyTimeout,
    inProgressJourneyTimeout,
    businessDaysUntilFutureTimeout,
    handleCreate,
    handleUpdate,
    handleCancel,
    handleResume,
  };
};

export default useJourneyTimeoutsApi;
